import { useState } from "react";
import { Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { fmtQty, fmtWhen, invApi, LEDGER_LABEL } from "./api";

export function LedgerList({ token, rows, onChanged, compact = false }) {
  const [busyId, setBusyId] = useState("");
  const remove = async (row, revert) => {
    const msg = revert
      ? `Undo this ${LEDGER_LABEL[row.type]?.text || row.type} of ${fmtQty(row.delta)} ${row.unit || ""} ${row.ingredient_name}? Stock will be put back the way it was.`
      : "Remove this log entry? Stock levels will NOT change.";
    if (!window.confirm(msg)) return;
    setBusyId(row.id);
    try {
      await invApi(token).delete(`/inventory/ledger/${row.id}`, { params: { revert } });
      toast.success(revert ? "Movement undone" : "Entry removed");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusyId("");
    }
  };
  if (!rows?.length) {
    return <div className="px-5 py-8 text-center text-sm text-stone-400" data-testid="ledger-empty">No stock movements yet.</div>;
  }
  return (
    <ul className="divide-y divide-stone-100" data-testid="ledger-list">
      {rows.map((r) => {
        const meta = LEDGER_LABEL[r.type] || { text: r.type, cls: "bg-stone-100 text-stone-700" };
        const neg = Number(r.delta) < 0;
        return (
          <li key={r.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`ledger-row-${r.id}`}>
            <span className={`shrink-0 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.text}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-stone-800 truncate">{r.ingredient_name}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {fmtWhen(r.created_at)}
                {r.order_number ? ` · #${r.order_number}` : ""}
                {r.note ? ` · ${r.note}` : ""}
                {!compact && r.balance_after != null ? ` · now ${fmtQty(r.balance_after)} ${r.unit || ""}` : ""}
              </div>
            </div>
            <div className={`font-black tabular-nums text-sm ${neg ? "text-red-600" : "text-emerald-700"}`}>
              {neg ? "" : "+"}{fmtQty(r.delta)} <span className="text-[10px] text-stone-400 font-bold">{r.unit}</span>
            </div>
            {!compact && (
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" title="Undo (put stock back)" disabled={busyId === r.id} onClick={() => remove(r, true)} data-testid={`ledger-undo-${r.id}`}
                  className="p-1.5 rounded-lg text-stone-400 hover:text-brand-red hover:bg-red-50"><Undo2 className="w-4 h-4" /></button>
                <button type="button" title="Remove entry only" disabled={busyId === r.id} onClick={() => remove(r, false)} data-testid={`ledger-delete-${r.id}`}
                  className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
